import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Mail, Clock, Facebook, Instagram, Youtube, Linkedin } from "lucide-react";

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    setIsSubmitting(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden text-white">
          <div className="absolute inset-0 z-0">
            <img
              src="assets/pexels-pixabay-87635.webp"
              alt="Contact Background"
              className="w-full h-full object-cover object-center sm:scale-110 sm:transition-transform sm:duration-1000"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/70"></div>
          </div>
          <div className="relative z-10 text-center px-4 sm:px-6 md:px-8 max-w-4xl mx-auto py-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight leading-tight">
              Driving Sales, Building Trust, Delivering Results
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-light max-w-2xl mx-auto leading-relaxed">
              Your trusted mandate partner for exclusive project marketing, seamless sales execution, and developer–buyer confidence.
            </p>
          </div>
        </section>

        {/* Google Map Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-[#741112] text-center">
              Find Us on the Map
            </h2>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3556.0814500560214!2d73.03070347493731!3d19.011043482180234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c3228a6c3c85%3A0x430a108a5916b3e2!2sRealation%20Property%20Consultant%20LLP!5e1!3m2!1sen!2sin!4v1752038315487!5m2!1sen!2sin"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="REALATION Location"
              ></iframe>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Form */}
            <div className="bg-white p-8 rounded-lg shadow">
              <h2 className="text-2xl font-bold mb-6 text-[#741112]">Send Us a Message</h2>

              <form
                action="/backend/submit.php"
                method="POST"
                encType="multipart/form-data"
                className="space-y-6"
                onSubmit={handleSubmit}
              >
                <input type="hidden" name="form_type" value="Contact Page Form" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700">First Name</label>
                    <input type="text" name="firstName" required className="w-full border rounded-md p-3 text-sm" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Last Name</label>
                    <input type="text" name="lastName" required className="w-full border rounded-md p-3 text-sm" />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Phone Number</label>
                  <input type="text" name="phone" required className="w-full border rounded-md p-3 text-sm" />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Email Address</label>
                  <input type="email" name="email" required className="w-full border rounded-md p-3 text-sm" />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Message (Optional)</label>
                  <textarea name="message" rows="4" className="w-full border rounded-md p-3 text-sm"></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#741112] hover:bg-[#5c0e0f] text-white font-semibold py-3 rounded-md w-full flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                  ) : null}
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-10">
              <div>
                <h2 className="text-2xl font-bold text-[#741112] mb-4">Get in Touch</h2>
                <p className="text-gray-600 mb-8">
                  Have questions about buying a home? Need help with your property search? Our team is ready to assist you.
                </p>

                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-[#741112]/10 p-3 rounded-full"><Phone className="text-[#741112]" /></div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#741112]">Phone</h3>
                      <p className="text-gray-600">+91 22 4660 4783</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="bg-[#741112]/10 p-3 rounded-full"><Mail className="text-[#741112]" /></div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#741112]">Email</h3>
                      <p className="text-gray-600">info@realation.in</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="bg-[#741112]/10 p-3 rounded-full"><Clock className="text-[#741112]" /></div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#741112]">Business Hours</h3>
                      <p className="text-gray-600">Monday - Sunday: 10:00 AM - 7:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div>
                <h3 className="text-lg font-semibold text-[#741112] mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/profile.php?id=100088421362780#"
                    className="bg-[#741112]/10 p-2 rounded-full text-[#741112] transition transform hover:bg-[#741112] hover:text-white hover:scale-110"
                  >
                    <Facebook size={20} />
                  </a>
                  <a
                    href="https://www.instagram.com/realation_official?igsh=MXBieGhqNXg4ZDB6YQ=="
                    className="bg-[#741112]/10 p-2 rounded-full text-[#741112] transition transform hover:bg-[#741112] hover:text-white hover:scale-110"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href="https://www.youtube.com/@realation6580"
                    className="bg-[#741112]/10 p-2 rounded-full text-[#741112] transition transform hover:bg-[#741112] hover:text-white hover:scale-110"
                  >
                    <Youtube size={20} />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/realation-official/"
                    className="bg-[#741112]/10 p-2 rounded-full text-[#741112] transition transform hover:bg-[#741112] hover:text-white hover:scale-110"
                  >
                    <Linkedin size={20} />
                  </a>
                </div>
              </div>
            </div>

          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default Contact;
